/**
 * The pure decisions behind the link form (docs/planning/link-artifacts.md,
 * D5): which title and which site icon a URL prefills, and what an edit
 * changed. No React, no fetch: the form and its tests share these.
 */

import { linkHostname } from '@bower/shared/utils/linkHref';

import type { UrlMetadata } from '../../../services/urlMetadata.service';

/** Which icon a link shows: its site's, or the Bower link glyph. */
export type LinkIconChoice = 'site' | 'bower';

/** Where a link's title came from, for the Link Created event. */
export type LinkTitleSource = 'page' | 'hostname' | 'user';

type PrefillMetadata = Pick<UrlMetadata, 'title' | 'favicon'> | null | undefined;

/**
 * Titles a bot challenge or interstitial serves instead of the page (the
 * server-side resolver is a bot to these sites). Matched on the whole title,
 * lowercased, with a trailing ellipsis ("..." or "…") ignored. Kept here, not
 * in the shared resolver, because chat sources use that resolver too.
 */
const INTERSTITIAL_TITLES = [
  /^client challenge$/,
  /^just a moment$/,
  /^attention required! \| cloudflare$/,
  /^access denied$/,
  /^please wait \| cloudflare$/,
  /^ddos-guard$/,
];

/** True when a resolved title is a bot challenge's, not the page's. */
export function isInterstitialTitle(title: string): boolean {
  const normalized = title
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/(\.\.\.|…)(?=$| \|)/g, '');
  return INTERSTITIAL_TITLES.some((pattern) => pattern.test(normalized));
}

/**
 * The page's own title when the resolver found one, else the hostname. Prism
 * Cloud is an SPA shell with no `<title>`, so it gets `app.graphpad.com`. A
 * bot challenge's title ("Just a moment...") counts as no title.
 */
export function chooseLinkTitle(
  url: string,
  metadata: PrefillMetadata,
): { readonly title: string; readonly source: 'page' | 'hostname' } {
  const pageTitle = metadata?.title?.trim();
  if (pageTitle && !isInterstitialTitle(pageTitle)) return { title: pageTitle, source: 'page' };
  return { title: linkHostname(url), source: 'hostname' };
}

/**
 * The resolver's favicon only when it is `https:`; otherwise the site's own
 * `/favicon.ico`. Prism Cloud serves a `data:` icon, so it gets
 * `https://app.graphpad.com/favicon.ico`. Null when the URL does not parse.
 */
export function chooseSiteIconUrl(url: string, metadata: PrefillMetadata): string | null {
  const favicon = metadata?.favicon;
  if (favicon && favicon.startsWith('https://')) return favicon;
  try {
    return `https://${new URL(url).host}/favicon.ico`;
  } catch {
    return null;
  }
}

/** A link as the form edits it. `favicon` null = the Bower icon. */
export interface LinkFieldValues {
  readonly url: string;
  readonly title: string;
  readonly favicon: string | null;
}

export type LinkChange = 'url' | 'title' | 'icon';

/**
 * What an edit sends: only what changed. `metadata.favicon` is a merge patch
 * on the server, so null (the Bower icon) clears the stored site icon.
 */
export function linkEditPatch(
  before: LinkFieldValues,
  after: LinkFieldValues,
): {
  readonly patch: {
    content?: { url: string };
    title?: string;
    metadata?: { favicon: string | null };
  };
  readonly changed: readonly LinkChange[];
} {
  const patch: {
    content?: { url: string };
    title?: string;
    metadata?: { favicon: string | null };
  } = {};
  const changed: LinkChange[] = [];
  if (after.url !== before.url) {
    patch.content = { url: after.url };
    changed.push('url');
  }
  if (after.title !== before.title) {
    patch.title = after.title;
    changed.push('title');
  }
  if (after.favicon !== before.favicon) {
    patch.metadata = { favicon: after.favicon };
    changed.push('icon');
  }
  return { patch, changed };
}
